import { type Col } from '@pequity/shared/ccp/types';
import { isProd } from '@pequity/shared/utils/env';
import * as Sentry from '@sentry/vue';
import { type App } from 'vue';
import { type Router } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { ENVIRONMENT } from '@/types/environment';

export const setupSentry = (app: App, router: Router) => {
  if (isProd) {
    Sentry.init({
      app,
      dsn: import.meta.env.VITE_DSN,
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration({
          stickySession: true,
          // https://docs.sentry.io/platforms/javascript/guides/vue/session-replay/configuration/#mutation-limits
          mutationBreadcrumbLimit: 500,
          mutationLimit: 1500,
        }),
      ],
      // Keep the restrictive v10 defaults. Leaving dataCollection unset in v11 collects HTTP bodies, cookies and user info.
      // https://github.com/getsentry/sentry-javascript/blob/develop/MIGRATION.md
      dataCollection: {
        userInfo: false,
        cookies: false,
        httpHeaders: {
          request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
          response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
        },
        httpBodies: [],
        urlQueryParams: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
        genAI: { inputs: false, outputs: false },
        databaseQueryData: false,
        queues: false,
        graphQL: { document: false, variables: false },
      },
      environment: ENVIRONMENT,
      // This sets the sample rate to be 20%
      tracesSampleRate: 0.2,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      ignoreErrors: [
        'Failed to fetch dynamically imported module',
        'error loading dynamically imported module',
        'Unable to preload CSS',
        'Tokens are invalid',
        'All tokens expired',
      ],
    });
  }
};

export const setSentryUser = (email: string) => {
  Sentry.setUser({ email });
};

export const captureContentTemplateError = (col: Col, error: unknown) => {
  const tenant = useAuthStore().profile?.company_schema_name ?? '';

  Sentry.captureException(error, {
    tags: { column: col.name, layout_id: col.id, tenant },
    fingerprint: ['{{ default }}', tenant, String(col.id)],
  });
};
